import { tw } from 'twind/css';
import { useRef } from 'react';

import { SanityImage } from 'ui/components/SanityImage';
import { Container } from 'ui/elements/LayoutContainer';
import { Prose } from 'utils/serializeBlockContent';
import { PortableText } from '__generated__/graphql';
import { isNotNullish } from 'utils/isNotNullish';
import { SanityImageObjectType } from 'groq/objects/SanityImageObject';
import { SanityVideoObjectType } from 'groq/objects/SanityVideoObject';
import { SanityVideo } from 'ui/components/SanityVideo';

export type GlossaryItem = {
  tags: {
    label: string;
    value: string;
  }[];
  definition: PortableText[];
  id: string;
  image: SanityImageObjectType;
  puid: string;
  term: string;
  video: SanityVideoObjectType;
};

/** Render the term, definition, and media for a given glossary item. */
export function GlossaryItem(
  props: GlossaryItem & { alignment: 'left' | 'right' }
) {
  const glossaryItemRef = useRef<any>(null);
  const flexDirection = props.alignment === 'right' ? 'row' : 'row-reverse';
  const isImage = isNotNullish(props.image);
  const isVideo = !isImage && isNotNullish(props.video?.source);

  return (
    <Container bleed data-test-id="glossary-item">
      <a
        className="absolute invisible -top-24 relative inline-block"
        id={props.term?.slice(0, 1).toLowerCase()}
      />
      <a
        className="absolute invisible -top-24 relative inline-block"
        id={props.puid}
      />
      <section
        className={tw(
          'bg-white flex flex-col relative',
          `sm:flex-${flexDirection}`
        )}
        ref={glossaryItemRef}
      >
        <div className="relative flex-none w-full sm:w-1/2 aspect-3-2">
          {isImage && <SanityImage source={props.image} />}
          {isVideo && (
            <SanityVideo
              className="absolute inset-0 h-full object-center object-cover w-full"
              video={props.video}
              playsInline
            />
          )}
        </div>
        <div className="w-full flex flex-col justify-center gap-4 mobile:p-6 tablet:p-8 desktop:p-10 xl:p-16">
          {props.term && (
            <h2
              data-test-id="glossary-item-term"
              className="max-w-[30ch] font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl"
            >
              {props.term}
            </h2>
          )}
          {props.definition && (
            <div className="max-w-[70ch]">
              <Prose content={props.definition} />
            </div>
          )}
        </div>
      </section>
    </Container>
  );
}
